import { useQuery } from '@tanstack/react-query'
import { Navigate, useLocation } from 'react-router-dom'

import { ROUTES } from '../constants'
import { getMyProfile } from '../pages/profile/apis'
import { isProfileComplete } from '../utils/profile'
import { userStorage } from '../utils/userStorage'

function ProfileCompletionGuard({ children }) {
  const location = useLocation()

  // Fast-path: the /me payload is mirrored into userStorage on login and
  // on every successful `refreshCurrentUserMe()` call, so the cached
  // snapshot is usually authoritative. Skipping the query here eliminates
  // a redundant `/me` round-trip on every protected-page mount.
  const cachedUser = userStorage.getUserDetails()

  // Always call useQuery (hooks must be unconditional). Only enable the
  // network round-trip when we don't already have a cached snapshot we can
  // use to decide whether to render or redirect.
  const profileQuery = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: getMyProfile,
    // Match the global default in src/hooks/queryClient.js so we don't
    // refetch on every minute when the cache is just mildly stale.
    staleTime: 5 * 60_000,
    enabled: !cachedUser,
  })

  // Fast-path: cached profile is complete → render children immediately.
  if (cachedUser && isProfileComplete(cachedUser)) {
    return children
  }

  // Cached profile is incomplete (the common post-login state for a user
  // who hasn't filled in their name yet). Do NOT render the protected page
  // here — that would briefly flash the wrong URL before the redirect
  // lands, producing a visible flicker between e.g. /branch-selection and
  // /profile. Redirect to /profile synchronously from the cached snapshot.
  // /profile itself is allowed to mount so the user can complete the form.
  if (cachedUser) {
    if (location.pathname === ROUTES.PROFILE) {
      return children
    }
    return (
      <Navigate
        to={ROUTES.PROFILE}
        replace
        state={{ profileIncomplete: true, attemptedPath: location.pathname }}
      />
    )
  }

  // No cached user at all (rare — e.g. the /me call already failed once and
  // userStorage was cleared). We have to round-trip /me before deciding,
  // but we still don't want to mount the protected page in the meantime,
  // since the same flicker would happen once the result lands. Show nothing
  // until the query resolves, then redirect if needed.
  if (profileQuery.isLoading) {
    return null
  }

  // Keep the application available if the profile endpoint is temporarily
  // unavailable. The profile page will render its existing error state.
  if (profileQuery.isError) {
    return children
  }

  if (!isProfileComplete(profileQuery.data) && location.pathname !== ROUTES.PROFILE) {
    return (
      <Navigate
        to={ROUTES.PROFILE}
        replace
        state={{ profileIncomplete: true, attemptedPath: location.pathname }}
      />
    )
  }

  return children
}

export { ProfileCompletionGuard }
export default ProfileCompletionGuard
